/* src/components/pages/Extraercesionesdeu.jsx - Modificado el 13-03-2026 */
/* Extraer Cesiones de Fa en sii como Tipoing=1: 'Consulta para el Deudor' */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';

import {
  TextField,
  Typography,
  MenuItem,
  Button,
  Box,
  Stack,
  LinearProgress
} from '@mui/material';

import api from "../../services/api.js";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { CustomToolbar } from "../../components/datagrid/CustomToolbar";
import DataGridSkeleton from "../../components/datagrid/DataGridSkeleton";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import { RiFileSearchLine } from "react-icons/ri";

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";


const Buscarcesionesdeu = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* ===============================
     Estados principales
  =============================== */

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Async Jobs */
  const [jobId, setJobId] = useState(null);

  /* Job trabajando */
  const scraperOcupado = jobId !== null;

  /* Progreso Async */
  const [progress, setProgress] = useState(null);
  const [progresoTexto, setProgresoTexto] = useState("");

  /* Server Pagination */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  const [tipo, setTipo] = useState('DEUDOR');

  /* Recuperar jobId cuando carga la página */
  useEffect(() => {
    const storedJob = localStorage.getItem("cesionesJobId");
    if (storedJob) {
      setJobId(storedJob);
      setProgresoTexto("🔄 Recuperando proceso en ejecución...");
    }
  }, []);


  /* Manejo del tipo */
  const handleTipoChange = (e) => {
    setTipo(e.target.value);
  };

  /* ===============================
     Validación Rut Cliente
  =============================== */

  const rutValido = useMemo(() => {
    if (!user?.usuario) return false;
    return validarRut(user.usuario);
  }, [user?.usuario]);

  /* ===============================
     Bitácora server-side
  =============================== */

  const fetchBitacora = useCallback(async () => {
    if (!rutValido) {
      showFeedback({
        message: 'El Rut ingresado no es válido',
        severity: 'error',
      });
      setBitacora([]);
      setRowCount(0);
      return;
    }

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const { data } = await api.post("/cesiones/getbitacora", {
        RutCli,
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
      
    } catch (error) {
      showFeedback({
        message: "❌ No fue posible obtener la bitácora",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [rutValido, user?.usuario, page, pageSize, showFeedback]);

  /* llamar a cargar bitacora */
  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  /* ===============================
    Polling Async Scraping Job
  =============================== */
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {

        const { data } = await api.get(
          `/extraercesiones/status/${jobId}`
        );

        /* progreso en vivo */

        if (data.progress && typeof data.progress === "object") {

          setProgress(data.progress);

          if (data.progress.message) {
            setProgresoTexto(data.progress.message);
          }

        }

        if (data.state === "active" && !progresoTexto) {
          setProgresoTexto("⏳ Iniciando extracción de cesiones...");
        }

        /* COMPLETADO */

        if (data.state === "completed") {

          clearInterval(interval);

          setProgresoTexto("🎉 Extracción completada");

          showFeedback({
            message: "✅ Extracción de cesiones finalizada",
            severity: "success",
          });

          await fetchBitacora();

          setTimeout(() => {
            setProgress(null);
            setProgresoTexto("");
          }, 4000);

          setJobId(null);
          localStorage.removeItem("cesionesJobId");

          return;
        }

        /* ERROR */

        if (data.state === "failed") {

          clearInterval(interval);

          setProgress(null);
          setProgresoTexto("");
          setJobId(null);
          localStorage.removeItem("cesionesJobId");

          showFeedback({
            message: "❌ Falló el scraping de cesiones",
            severity: "error",
          });

          return;
        }

      } catch {

        clearInterval(interval);

        setProgress(null);
        setProgresoTexto("");
        setJobId(null);
        localStorage.removeItem("cesionesJobId");

        showFeedback({
          message: "❌ No fue posible consultar estado del Job",
          severity: "error",
        });

      }

    }, 2500);

    return () => clearInterval(interval);

  }, [jobId, progresoTexto, fetchBitacora, showFeedback]);



  /* ===============================
     DataGrid Config
  =============================== */

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "fecha",
        headerName: "Fecha",
        width: 200,
        align: "center",
        headerAlign: "center",
        renderCell: (p) =>
          p.value ? new Date(p.value).toLocaleString("es-CL") : "—",
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutcli",
        headerName: "Rut Cliente",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "nombre",
        headerName: "Razon Social",
        width: 300,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "detalle",
        headerName: "Detalle",
        flex: 1,
        minWidth: 400,
      },
    ],
    []
  );


  /* ===============================
    Solicitar scraping async
  =============================== */

  const solicitarExtraccion = async () => {

    if (!rutValido) {
      showFeedback({
        message: "El Rut del cliente no es válido",
        severity: "error",
      });
      return;
    }

    try {

      const RutCli = normalizeRut(user.usuario);

      const tipoMap = {
        DEUDOR: 1,
        CEDENTE: 2,
        CESIONARIO: 3
      };

      const Tipoing = tipoMap[tipo];

      showFeedback({
        message: "⏳ Iniciando scraping de cesiones...",
        severity: "info",
      });

      const { data } = await api.post("/extraercesiones/start", {
        RutCli,
        Tipoing
      });

      if (data?.success) {

        setProgress(null);
        setProgresoTexto("");
        setJobId(data.jobId);
        localStorage.setItem("cesionesJobId", data.jobId);

        showFeedback({
          message: "📌 Proceso ejecutándose en segundo plano",
          severity: "info",
        });

      } else {

        showFeedback({
          message: data?.message || "No fue posible iniciar extracción",
          severity: "error",
        });

      }

    } catch {

      showFeedback({
        message: "❌ Error al iniciar scraping de cesiones",
        severity: "error",
      });

    }
  };

  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      {/* Barra controles */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        {/* Rut Cliente */}
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Nombre Cliente */}
        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Campo de tipo */}
        <TextField
          select
          id="rut-tipo"
          label="Tipo"
          value={tipo}
          onChange={handleTipoChange}
          variant="outlined"
          disabled={scraperOcupado}
          fullWidth
          required
          sx={{
            width: 150,
            height: '40px', // Altura del TextField
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': { textAlign: 'center',},
          }}
        >
          <MenuItem value="DEUDOR">DEUDOR</MenuItem>
          <MenuItem value="CEDENTE">CEDENTE</MenuItem>
          <MenuItem value="CESIONARIO">CESIONARIO</MenuItem>
        </TextField>


        {/* Botón Solicitar */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine size={18} />}
          disabled={scraperOcupado}
          onClick={() => {
            if (scraperOcupado) return;
            setPage(0);
            setRowCount(0);
            solicitarExtraccion();
          }}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,

            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            color: (theme) => theme.custom.ui.primaryActionText,

            '&:hover': {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          {scraperOcupado ? "Procesando..." : "Solicitar"}
        </Button>

      </Stack>

      {/* Progreso Async */}
      {jobId && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {progresoTexto || "⏳ Ejecutando extracción..."}
          </Typography>

          {progress?.current && progress?.total && (
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 200px)', // ajusta según tu layout
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={bitacora}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);
          }}

          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} registros encontrados
      </Typography>
    </Box>
  );
};

export default Buscarcesionesdeu;
